import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import en from './locales/en';
import es from './locales/es';
import fr from './locales/fr';
import zh from './locales/zh';
import {SupportedLocale, TranslationTree} from './types';
import {detectInitialLocale, FALLBACK_LOCALE, LOCALE_STORAGE_KEY, normalizeLocale, SUPPORTED_LOCALES} from './locale';

type TranslateOptions = Record<string, string | number>;

interface I18nContextValue {
    locale: SupportedLocale;
    setLocale: (nextLocale: string) => void;
    t: (key: string, options?: TranslateOptions) => string;
    supportedLocales: SupportedLocale[];
}

const dictionaries: Record<SupportedLocale, TranslationTree> = {
    en,
    es,
    fr,
    zh,
};

const I18nContext = createContext<I18nContextValue>({
    locale: FALLBACK_LOCALE,
    setLocale: () => undefined,
    t: (key: string) => key,
    supportedLocales: SUPPORTED_LOCALES,
});

const interpolate = (template: string, options?: TranslateOptions): string => {
    if (!options) {
        return template;
    }

    return Object.entries(options).reduce((acc, [name, value]) => {
        return acc.replace(new RegExp(`{{\\s*${name}\\s*}}`, 'g'), String(value));
    }, template);
};

const resolveNode = (tree: TranslationTree, key: string): string | undefined => {
    return key.split('.').reduce<string | TranslationTree | undefined>((acc, segment) => {
        if (!acc || typeof acc === 'string') {
            return undefined;
        }

        return acc[segment];
    }, tree) as string | undefined;
};

export const I18nProvider = ({children}: {children: React.ReactNode}) => {
    const [locale, setLocaleState] = useState<SupportedLocale>(detectInitialLocale);

    const setLocale = useCallback((nextLocale: string) => {
        const normalized = normalizeLocale(nextLocale);
        setLocaleState(normalized);

        if (typeof window !== 'undefined') {
            window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
        }
    }, []);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.lang = locale;
        }
    }, [locale]);

    const t = useCallback((key: string, options?: TranslateOptions): string => {
        const activeValue = resolveNode(dictionaries[locale], key);
        const fallbackValue = resolveNode(dictionaries[FALLBACK_LOCALE], key);
        const message = typeof activeValue === 'string' ? activeValue : (typeof fallbackValue === 'string' ? fallbackValue : key);

        return interpolate(message, options);
    }, [locale]);

    const value = useMemo<I18nContextValue>(() => ({
        locale,
        setLocale,
        t,
        supportedLocales: SUPPORTED_LOCALES,
    }), [locale, setLocale, t]);

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    );
};

export const useI18n = (): I18nContextValue => useContext(I18nContext);

export {LOCALE_STORAGE_KEY, SUPPORTED_LOCALES, FALLBACK_LOCALE, normalizeLocale, detectInitialLocale};
